import React, { createContext, useContext, useEffect, useState } from 'react';
import apiService from '../services/api';
import { useToast } from '../hooks/use-toast';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({
  children
}) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    toast
  } = useToast();

  // Token expiration check (matching Angular auth service)
  const getTokenExpireTime = () => {
    const tokenExpireTimeString = localStorage.getItem('authTokenExpiration');
    if (tokenExpireTimeString) {
      return parseInt(tokenExpireTimeString, 10);
    }
    return 0;
  };

  const isTokenExpired = () => {
    const currentTimestamp = new Date().getTime();
    const tokenExpireTime = getTokenExpireTime();
    return currentTimestamp >= tokenExpireTime;
  };

  const refreshTokenIfNeeded = async () => {
    if (isTokenExpired()) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await apiService.refreshToken(refreshToken);
          if (response.data.access_token) {
            localStorage.setItem('access_token', response.data.access_token);
            // Set token expiration (default 1 hour from now)
            const expirationTime = new Date().getTime() + (60 * 60 * 1000);
            localStorage.setItem('authTokenExpiration', expirationTime.toString());
            return true;
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          logout();
          return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        // Check token expiration and refresh if needed
        const isValid = await refreshTokenIfNeeded();
        if (isValid) {
          // Fetch user data
          try {
            const userResponse = await apiService.getUserData();
            setUser({
              id: userResponse.data.id || '1',
              username: userResponse.data.username || userResponse.data.email?.split('@')[0] || 'user',
              email: userResponse.data.email || 'user@example.com',
              role: userResponse.data.role || 'user',
              full_name: userResponse.data.full_name || '',
              is_active: userResponse.data.is_active !== false
            });
          } catch (error) {
            // If user data fetch fails, set basic user from token
            const username = localStorage.getItem('username') || 'user';
            setUser({
              id: '1',
              username: username,
              email: `${username}@example.com`,
              role: 'user',
              full_name: '',
              is_active: true
            });
          }
        }
      }
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setIsLoading(true);
      const response = await apiService.login({
        username,
        password
      });
      
      if (response.data.access_token) {
        // Store tokens
        localStorage.setItem('access_token', response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        
        // Store username for later use
        localStorage.setItem('username', username);
        
        // Set token expiration (default 1 hour from now)
        const expirationTime = new Date().getTime() + (60 * 60 * 1000);
        localStorage.setItem('authTokenExpiration', expirationTime.toString());

        // Fetch user data after login
        try {
          const userResponse = await apiService.getUserData();
          setUser({
            id: userResponse.data.id || '1',
            username: userResponse.data.username || username,
            email: userResponse.data.email || `${username}@example.com`,
            role: userResponse.data.role || 'user',
            full_name: userResponse.data.full_name || '',
            is_active: userResponse.data.is_active !== false
          });
        } catch (error) {
          // Fallback user data
          setUser({
            id: response.data.user?.id || '1',
            username: response.data.user?.username || username,
            email: response.data.user?.email || `${username}@example.com`,
            role: response.data.user?.role || 'user',
            full_name: response.data.user?.full_name || '',
            is_active: true
          });
        }
        
        toast({
          title: "Success",
          description: "Logged in successfully"
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.response?.data?.message || "Login failed",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all auth-related storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('authTokenExpiration');
    localStorage.removeItem('username');
    sessionStorage.removeItem('authToken');
    
    setUser(null);
    toast({
      title: "Logged out",
      description: "You have been logged out successfully"
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshTokenIfNeeded,
    isTokenExpired
  };

  return <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>;
};